import React, { useState, useEffect } from 'react';
import { View } from 'react-native';
import { useQuery, useMutation } from '@apollo/client';
import { useForm } from 'react-hook-form';

import Alert from 'app/src/elements/Alert';
import { SUGGEST_MEDICINES } from 'app/src/components/Medicines/queries';
import {
  Container,
  ButtonContainer,
  EmojiText,
  LabelText,
} from 'app/src/elements/ButtonPicker';
import { Fields, defaultValues, prepareInput } from 'app/src/elements/forms';
import { Button } from 'app/src/elements/buttons';
import { Loading } from 'app/src/elements/DataState';
import { Title, Subtitle } from 'app/src/styles';
import { useNavigate, useParams } from 'app/src/utils/routing';

import { MEDICINE_GROUP, UPDATE_MEDICINE_GROUP, DESTROY_MEDICINE_GROUP } from './queries';
import { FIELDS } from './new';

const MedicineGroup = () => {
  const { id } = useParams();
  const [formStatus, setFormStatus] = useState();
  const [selectedMedicineIds, setSelectedMedicineIds] = useState([]);
  const navigate = useNavigate();

  const { control, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: defaultValues({ fields: FIELDS }),
  });

  const { data } = useQuery(MEDICINE_GROUP, { variables: { id } });

  useEffect(() => {
    if (data?.medicineGroup) {
      reset(defaultValues({ fields: FIELDS, resource: data.medicineGroup }));
      setSelectedMedicineIds(
        data.medicineGroup.medicines.map(m => m.id)
      );
    }
  }, [data]);

  const clientId = data?.medicineGroup?.client?.id || null;

  const { data: medicinesData } = useQuery(SUGGEST_MEDICINES, {
    variables: { clientId, all: true },
    skip: !clientId,
  });

  const [updateMedicineGroup] = useMutation(UPDATE_MEDICINE_GROUP, {
    onCompleted: () => navigate('/medicine-groups'),
  });

  const [destroyMedicineGroup] = useMutation(DESTROY_MEDICINE_GROUP, {
    onCompleted: () => navigate('/medicine-groups'),
  });

  const medicines = medicinesData?.medicines || [];

  const toggleMedicine = medicineId => {
    setSelectedMedicineIds(prev =>
      prev.includes(medicineId)
        ? prev.filter(mid => mid !== medicineId)
        : [...prev, medicineId]
    );
  };

  const onSubmit = ({ clientId, ...input }) => {
    setFormStatus('submitting');
    updateMedicineGroup({
      variables: {
        input: {
          id,
          ...prepareInput(input, FIELDS),
          medicineGroupMedicinesAttributes: selectedMedicineIds.map(
            medicineId => ({ medicineId })
          ),
        },
      },
    });
  };

  const handleDestroy = () => {
    Alert.alert(
      'Delete this medicine group?',
      data?.medicineGroup?.name,
      [
        { text: 'No', onPress: () => false, style: 'cancel' },
        {
          text: 'Yes',
          onPress: () => destroyMedicineGroup({ variables: { input: { id } } }),
        },
      ]
    );
  };

  return (
    <View>
      <Title>{data?.medicineGroup?.name}</Title>

      <Fields fields={FIELDS} control={control} errors={errors} />

      <Subtitle>Medicines</Subtitle>
      <Container>
        {medicines.map(medicine => (
          <ButtonContainer
            key={medicine.id}
            onPress={() => toggleMedicine(medicine.id)}
            notSelected={!selectedMedicineIds.includes(medicine.id)}
          >
            <EmojiText>{medicine.emoji || medicine.name[0]}</EmojiText>
            <LabelText>{medicine.name}</LabelText>
          </ButtonContainer>
        ))}
      </Container>

      {'submitting' === formStatus ? (
        <Loading />
      ) : (
        <Button label="Save" onPress={handleSubmit(onSubmit)} wide />
      )}

      <Button label="Delete Group" onPress={handleDestroy} wide />
    </View>
  );
};

export default MedicineGroup;
